<template>
  <div class="p-5 bg-white rounded non-print">
    <div class="py-6 text-xl text-center">{{ `${$t('appeal.appeal_list')} (${currentAppeals.count || 0} ${$t('list')})` }}</div>
    <between-date @onClick="initData" :isLoading="isLoading"></between-date>
    <div class="flex justify-space-between py-3">
      <div class="flex align-center">
        <div class="mr-3">{{ $t('timeunit.time_format') }} :</div>
        <v-btn-toggle v-model="isFullTimeFormat" color="info">
          <v-btn :value="true" style="width:200px;">{{ $t('timeunit.time_format_day_hour_minute_second') }}</v-btn>
          <v-btn :value="false" style="width:200px;">{{ $t('timeunit.minute.long') }}</v-btn>
        </v-btn-toggle>
      </div>
      <v-btn class="blue white--text mx-1" @click="exportExcel" :disabled="!currentAppeals.count">
        <v-icon left>mdi-file-excel</v-icon>Export Excel
      </v-btn>
    </div>
    <v-overlay v-show="isLoading" opacity="0.5" color="black">
      <v-progress-circular :size="100" :value="20" indeterminate color="green"></v-progress-circular>
    </v-overlay>
    <div id="printarea" v-if="currentAppeals && currentAppeals.all && !isLoading">
      <div class="my-5">
        <div class="row border-t-4 border-b-4">
          <div class="col-2 text-center">{{ $t('appeal.appeal_category') }}</div>
          <div class="col-2 text-center">{{ $t('appeal.appeal_type') }}</div>
          <div class="col text-center text-green-500">{{ $t('finish') }}</div>
          <div class="col text-center text-yellow-600">{{ $t('waiting_for_processing') }}</div>
          <div class="col text-center text-red-600">{{ $t('cancel') }}</div>
          <div class="col text-center">{{ $t('period') }}*</div>
          <div class="col text-center">{{ $t('period') }}*({{ $t('average') }})</div>
        </div>
        <div class="row" v-for="(subCategoryItem, subCategory) in AppealLabels.subCategory" :key="subCategory">
          <div :class="`col-2 ${subCategoryItem.categoryShow ? 'border-t-2' : ''}`">{{ subCategoryItem.categoryShow ?
            $t(`appeal_data.category.${AppealLabels.category[subCategoryItem.category].value}`) : '' }}</div>
          <div class="col-2 border-t-2">{{ $t(`appeal_data.subCategory.${AppealLabels.subCategory[subCategoryItem.value].value}`) }}</div>
          <div class="col text-center border-t-2">
            <template
              v-if="currentAppeals && currentAppeals[subCategoryItem.category] && currentAppeals[subCategoryItem.category][subCategory] && currentAppeals[subCategoryItem.category][subCategory].success && currentAppeals[subCategoryItem.category][subCategory].success.all">{{
                currentAppeals[subCategoryItem.category][subCategory].success.all.length }}</template>
            <template v-else>-</template>
          </div>
          <div class="col text-center border-t-2">
            <template
              v-if="currentAppeals && currentAppeals[subCategoryItem.category] && currentAppeals[subCategoryItem.category][subCategory] && currentAppeals[subCategoryItem.category][subCategory].doing">{{
                currentAppeals[subCategoryItem.category][subCategory].doing.length }}</template>
            <template v-else>-</template>
          </div>
          <div class="col text-center border-t-2">
            <template
              v-if="currentAppeals && currentAppeals[subCategoryItem.category] && currentAppeals[subCategoryItem.category][subCategory] && currentAppeals[subCategoryItem.category][subCategory].cancelled && currentAppeals[subCategoryItem.category][subCategory].cancelled.all">{{
                currentAppeals[subCategoryItem.category][subCategory].cancelled.all.length }}</template>
            <template v-else>-</template>
          </div>
          <div class="col text-center border-t-2">
            <template
              v-if="currentAppeals && currentAppeals[subCategoryItem.category] && currentAppeals[subCategoryItem.category][subCategory] && currentAppeals[subCategoryItem.category][subCategory].time && currentAppeals[subCategoryItem.category][subCategory].time.successSec >= 0">{{
                isFullTimeFormat
                ? Helper.dhm(currentAppeals[subCategoryItem.category][subCategory].time.successSec) :
                currentAppeals[subCategoryItem.category][subCategory].time.success }}</template>
            <template v-else>-</template>
          </div>
          <div class="col text-center border-t-2">
            <template
              v-if="currentAppeals && currentAppeals[subCategoryItem.category] && currentAppeals[subCategoryItem.category][subCategory]">{{
                currentAppeals[subCategoryItem.category][subCategory][isFullTimeFormat
                  ? 'avgFormat' : 'avg'] }}</template>
            <template v-else>-</template>
          </div>
        </div>
        <div class="row border-t-4 border-b-4">
          <div class="col-4 text-right">{{ $t('sum') }}</div>
          <div :class="`col text-center ${currentAppeals.successCount ? 'text-green-500' : ''}`">
            {{ currentAppeals.successCount }}</div>
          <div :class="`col text-center ${currentAppeals.doingCount ? 'text-yellow-600' : ''}`">
            {{ currentAppeals.doingCount }}</div>
          <div :class="`col text-center ${currentAppeals.cancelledCount > 0 ? 'text-red-600' : ''}`">
            {{ currentAppeals.cancelledCount }}</div>
          <div class="col text-center">{{ isFullTimeFormat ? Helper.dhm(currentAppeals.time.successSec) :
            currentAppeals.time.success }}</div>
          <div class="col text-center">{{ isFullTimeFormat ? Helper.dhm(currentAppeals.avgSec) : currentAppeals.avg }}
          </div>
        </div>
      </div>
      <div class="py-5 flex text-gray-500">
        <div class="inline-flex font-weight-bold">{{ $t('note') }} :</div>
        <div class="pl-2">
          <div v-if="isFullTimeFormat">
            {{ $t('period') }}
            <span class="text-red-700">*</span> {{ $t('validate.time_format_mean') }}
          </div>
          <div v-else>
            {{ $t('period') }}
            <span class="text-red-700">*</span> {{ $t('validate.mean_minute') }}
          </div>
        </div>
      </div>
      <div class="mt-5" v-if="currentAppeals && currentAppeals.count > 0">
        <v-list-group color="info" prepend-icon="mdi-format-list-bulleted" class="border-t-2 border-b-2"
          v-model="showDetail">
          <template v-slot:activator>
            <v-list-item-title class="text-center">
              <div>{{ showDetail ? $t('appeal.hide_appeal_detail') : $t('appeal.show_appeal_detail') }}</div>
            </v-list-item-title>
          </template>
          <div class="w-100 text-center text-gray-600 bg-gray-100 p-5 border-t-2" style="font-size:25px;">
            {{ $t('appeal.appeal_detail_count', { count: currentAppeals.count || 0 }) }}</div>
          <v-data-table
            v-if="dataTable && dataTable.data"
            :headers="dataTable.headers"
            :items="dataTable.data"
            :items-per-page="20"
            class="elevation-1 v-data-table-report"
            ref="dataTableAppeals"
            :no-data-text="$t('noinfo')"
            :no-results-text="$t('no_matching_records_found')">
            <template v-slot:body.prepend>
              <tr>
                <td class="text-center">
                  <v-icon>mdi-filter-multiple-outline</v-icon>
                </td>
                <td class="ps-0">
                  <v-select v-model="filterData.subCategory" :items="filterOption.subCategory" type="text"
                    :label="$t('appeal.appeal_type')" solo dense hide-details clearable :no-data-text="$t('noinfo')">
                    <template v-slot:selection="{ item }">
                      {{ item }}
                    </template>
                    <template v-slot:item="{ item }">
                      {{ item }}
                    </template>
                  </v-select>
                </td>
                <td class="ps-0">
                  <v-select v-model="filterData.createdByText" :items="filterOption.createdBy" type="text"
                    :label="$t('created_by')" solo dense hide-details clearable :no-data-text="$t('noinfo')"/>
                </td>
                <td class="ps-0">
                  <v-select v-model="filterData.pendingByText" :items="filterOption.pendingBy" type="text"
                    :label="$t('task.send_approve_by')" solo dense hide-details clearable :no-data-text="$t('noinfo')"/>
                </td>
                <td class="ps-0">
                  <v-select v-model="filterData.approvedByText" :items="filterOption.approvedBy" type="text"
                    :label="$t('approve_by')" solo dense hide-details clearable :no-data-text="$t('noinfo')" />
                </td>
                <td class="ps-0">
                  <v-select v-model="filterData.successByText" :items="filterOption.successBy" type="text"
                    :label="$t('finish_by')" solo dense hide-details clearable :no-data-text="$t('noinfo')"/>
                </td>
                <td class="ps-0">
                  <v-select v-model="filterData.cancelledByText" :items="filterOption.cancelledBy" type="text"
                    :label="$t('cancel_by')" solo dense hide-details clearable :no-data-text="$t('noinfo')"/>
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  <v-select v-model="filterData.status" :items="filterOption.status" type="text" :label="$t('status')" solo dense
                    hide-details clearable :no-data-text="$t('noinfo')">
                    <template v-slot:selection="{ item }">
                      {{ item }}
                    </template>
                    <template v-slot:item="{ item }">
                      {{ item }}
                    </template>
                  </v-select>
                </td>
              </tr>
            </template>
            <template v-slot:item.createdAtText="{ item }">
              <div class="text-nowrap-table date">{{ item.createdAtText }}</div>
            </template>
            <template v-slot:item.subCategoryText="{ item }">
              <div class="text-nowrap-table">
                {{ item.subCategoryText }}
              </div>
            </template>
            <template v-slot:item.createdByText="{ item }">
              <div class="text-nowrap-table customer-name">{{ item.createdByText }}</div>
            </template>
            <template v-slot:item.statusPendingByText="{ item }">
              <div class="text-nowrap-table customer-name">{{ item.statusPendingByText }}</div>
            </template>
            <template v-slot:item.statusApprovedByText="{ item }">
              <div class="text-nowrap-table customer-name">{{ item.statusApprovedByText }}</div>
            </template>
            <template v-slot:item.statusSuccessByText="{ item }">
              <div class="text-nowrap-table customer-name">{{ item.statusSuccessByText }}</div>
            </template>
            <template v-slot:item.statusCancelledByText="{ item }">
              <div class="text-nowrap-table customer-name">{{ item.statusCancelledByText }}</div>
            </template>
            <template v-slot:item.statusText="{ item }">
              <v-chip :color="AppealLabels.status[item.status].color">
                {{ item.statusText }}
              </v-chip>
            </template>
          </v-data-table>
        </v-list-group>
      </div>
    </div>
    <div v-else-if="(!currentAppeals || !currentAppeals.all) && !isLoading" class="text-center text-gray-400 py-20"
      style="font-size:22px;">{{ $t('appeal.no_appeal_data') }}</div>
  </div>
</template>

<script>
import AppealLabels from "../json/appeal-data.json";
import { mapGetters, mapState } from "vuex";
import { ExportMarketReport } from "../exports";
import BetweenDate from "./BetweenDate.vue";
import Helper from "../plugins/Helper/Helper";
export default {
  components: {
    BetweenDate
  },
  computed: {
    ...mapGetters({
      appeals: "appeal/getAppeals",
      getMarketName: "markets/getMarketName"
    }),
    userFullName() {
      return this.$store.getters["user/getFullName"];
    },
    dataTable() {
      return {
        headers: [
          {
            text: this.$t('date'),
            value: "createdAtText",
            align: "center",
            width: "8%"
          },
          {
            text: this.$t('appeal.appeal_type'),
            width: "10%",
            value: "subCategoryText",
            align: "center",
            filter: d =>
              this.filterData.subCategory ? d == this.filterData.subCategory : d
          },
          {
            text: this.$t('created_by'),
            width: "10%",
            value: "createdByText",
            align: "center",
            filter: d =>
              this.filterData.createdByText
                ? d == this.filterData.createdByText
                : d
          },
          {
            text: this.$t('task.send_approve_by'),
            width: "10%",
            value: "statusPendingByText",
            align: "center",
            filter: d =>
              this.filterData.pendingByText
                ? d == this.filterData.pendingByText
                : d
          },
          {
            text: this.$t('approve_by'),
            width: "10%",
            value: "statusApprovedByText",
            align: "center",
            filter: d =>
              this.filterData.approvedByText
                ? d == this.filterData.approvedByText
                : d
          },
          {
            text: this.$t('finish_by'),
            width: "10%",
            value: "statusSuccessByText",
            align: "center",
            filter: d =>
              this.filterData.successByText
                ? d == this.filterData.successByText
                : d
          },
          {
            text: this.$t('cancel_by'),
            width: "10%",
            value: "statusCancelledByText",
            align: "center",
            filter: d =>
              this.filterData.cancelledByText
                ? d == this.filterData.cancelledByText
                : d
          },
          {
            text: this.$t('task.appeal_time_to_create_approve'),
            width: "10%",
            value: this.isFullTimeFormat
              ? "time.pendingFormat"
              : "time.pending",
            align: "center"
          },
          {
            text: this.$t('task.appeal_time_to_approve_process'),
            width: "10%",
            value: this.isFullTimeFormat
              ? "time.approveFormat"
              : "time.approve",
            align: "center"
          },
          {
            text: this.$t('appeal.appeal_time_to_process_finish'),
            width: "10%",
            value: this.isFullTimeFormat
              ? "time.successFormat"
              : "time.success",
            align: "center"
          },
          {
            text: this.$t('customer.time_summary_create_to_finish'),
            width: "10%",
            value: this.isFullTimeFormat ? "time.doneFormat" : "time.done",
            align: "center"
          },
          {
            text: this.$t('status'),
            value: "statusText",
            width: "8%",
            align: "center",
            filter: d =>
              this.filterData.status ? d == this.filterData.status : d
          }
        ],
        data: this.appeals
      };
    }
  },
  data() {
    return {
      Helper,
      AppealLabels,
      projectId: this.$route.params.projectId,
      marketId: this.$route.params.marketId,
      selected: [],
      isLoading: false,
      detailDialog: false,
      currentAppeal: null,
      isInvoiceDialog: false,
      showDetail: false,
      currentAppeals: {},
      currentInvoiceId: "",
      currentAppealId: "",
      isFullTimeFormat: true,
      startDate: this.$moment()
        .startOf("month")
        .format("YYYY-MM-DD"),
      endDate: this.$moment()
        .endOf("month")
        .format("YYYY-MM-DD"),
      itemsPerPage: 10,
      filterData: {
        subCategory: null,
        createdByText: null,
        pendingByText: null,
        approvedByText: null,
        successByText: null,
        cancelledByText: null,
        status: null
      },
      filterOption: {
        subCategory: [],
        status: [],
        createdBy: [],
        pendingBy: [],
        approvedBy: [],
        successBy: [],
        cancelledBy: []
      }
    };
  },
  mounted() {
    this.initData();
  },
  watch: {
    detailDialog(value) {
      if (!value) {
        this.currentAppeal = null;
        this.currentInvoiceId = "";
        this.currentAppealId = "";
      }
    },
    filterData: {
      handler(val) {
        if (
          this.$refs.dataTableAppeals &&
          this.$refs.dataTableAppeals.$children[0]
        ) {
          this.initDataAppeals(
            this.$refs.dataTableAppeals.$children[0].filteredItems
          );
        }
      },
      deep: true
    }
  },
  methods: {
    async initData(date) {
      this.isLoading = true;
      this.selected = [];
      if(date){
        this.startDate = this.$moment(date.startDate).format("YYYY-MM-DD")
        this.endDate =  this.$moment(date.endDate).format("YYYY-MM-DD")
      }
      await this.$store.dispatch("appeal/getAppealBetweenDateByMarket", {
        marketId: this.marketId,
        startDate: date
          ? date.startDate
          : this.$moment(this.startDate).toISOString(),
        endDate: date ? date.endDate : this.$moment(this.endDate).toISOString()
      });

      this.initDataAppeals(this.appeals);
      if (this.currentAppealId && this.appeals && this.appeals.length) {
        this.currentAppeal = this.appeals.find(
          booking => booking.objectId == this.currentAppealId
        );
      }
      this.isLoading = false;
    },
    async exportExcel() {
      let exportItems = [];
      exportItems =
        this.currentAppeals.count > 0 ? this.currentAppeals.all : [];
      try {
        await ExportMarketReport.exportMarketAppealReportByMarket(
          exportItems,
          this.getMarketName,
          this.startDate,
          this.endDate
        );
      } catch (e) {
        console.log(e);
      }
    },
    clickDetail(booking) {
      this.detailDialog = true;
      this.currentAppeal = booking;
      this.currentAppealId = booking.objectId;
    },
    openInvoice(invoiceId) {
      this.currentInvoiceId = invoiceId;
      this.isInvoiceDialog = true;
    },
    onSaveSuccess() {
      this.initData();
    },
    sumField(data, key, key2) {
      if (data && data.length && key && key2)
        return data.reduce((a, b) => a + (b[key] * b[key2] || 0), 0);
      else if (data && data.length && key && !key2)
        return data.reduce((a, b) => a + (b[key] || 0), 0);
      else return 0;
    },
    initDataAppeals(appeals = []) {
      let appealsOb = {
        all: [],
        time: {},
        timeUnit: "minutes",
        timeType: "seconds",
        avg: 0,
        avgSec: 0,
        count: 0,
        successCount: 0,
        doingCount: 0,
        cancelledCount: 0
      };
      for (const appeal of appeals) {
        //init filter option
        if (
          !this.filterOption.createdBy.find(
            cd => cd.text == appeal.createdByText
          ) &&
          appeal.createdByText
        ) {
          this.filterOption.createdBy.push(appeal.createdByText);
        }
        if (
          !this.filterOption.pendingBy.find(
            cd => cd.text == appeal.statusPendingByText
          ) &&
          appeal.statusPendingByText
        ) {
          this.filterOption.pendingBy.push(appeal.statusPendingByText);
        }
        if (
          !this.filterOption.approvedBy.find(
            cd => cd.text == appeal.statusApprovedByText
          ) &&
          appeal.statusApprovedByText
        ) {
          this.filterOption.approvedBy.push(appeal.statusApprovedByText);
        }
        if (
          !this.filterOption.successBy.find(
            cd => cd.text == appeal.statusSuccessByText
          ) &&
          appeal.statusSuccessByText
        ) {
          this.filterOption.successBy.push(appeal.statusSuccessByText);
        }
        if (
          !this.filterOption.cancelledBy.find(
            cd => cd.text == appeal.statusCancelledByText
          ) &&
          appeal.statusCancelledByText
        ) {
          this.filterOption.cancelledBy.push(appeal.statusCancelledByText);
        }
        if (
          !this.filterOption.status.find(cd => cd.text == appeal.statusText) &&
          appeal.statusText
        ) {
          this.filterOption.status.push(appeal.statusText);
        }
        if (
          !this.filterOption.subCategory.find(
            cd => cd.text == appeal.subCategoryText
          ) &&
          appeal.subCategoryText
        ) {
          this.filterOption.subCategory.push(appeal.subCategoryText);
        }

        let category = appeal.category;
        let subCategory = appeal.subCategory;
        let status = appeal.status;
        let time = appeal.time;
        appeal.time = time;
        if (!appealsOb[category])
          appealsOb[category] = {
            all: [],
            time: {}
          };
        if (!appealsOb[category][subCategory])
          appealsOb[category][subCategory] = {
            all: [],
            time: {},
            doing: [],
            avg: 0,
            avgSec: 0,
            avgFormat: ""
          };
        if (!appealsOb[category][subCategory][status])
          appealsOb[category][subCategory][status] = {
            all: [],
            time: {}
          };
        for (const idx in time) {
          if (time[idx]) {
            if (!appealsOb.time[idx]) appealsOb.time[idx] = 0;
            appealsOb.time[idx] += time[idx] || 0;
            if (!appealsOb[category].time[idx])
              appealsOb[category].time[idx] = 0;
            appealsOb[category].time[idx] += time[idx] || 0;
            if (!appealsOb[category][subCategory].time[idx])
              appealsOb[category][subCategory].time[idx] = 0;
            appealsOb[category][subCategory].time[idx] += time[idx] || 0;
            if (!appealsOb[category][subCategory][status].time[idx])
              appealsOb[category][subCategory][status].time[idx] = 0;
            appealsOb[category][subCategory][status].time[idx] +=
              time[idx] || 0;
          }
        }
        appealsOb.all.push(appeal);
        appealsOb[category].all.push(appeal);
        appealsOb[category][subCategory].all.push(appeal);
        appealsOb[category][subCategory][status].all.push(appeal);
        appealsOb.count++;
        if (
          status == "waiting" ||
          status == "pending" ||
          status == "approved"
        ) {
          appealsOb.doingCount++;
          appealsOb[category][subCategory].doing.push(appeal);
        }
        if (status == "cancelled") {
          appealsOb.cancelledCount++;
        }
        if (status == "success") {
          appealsOb.successCount++;
          appealsOb[category][subCategory].avg = Helper.roundNumber(
            appealsOb[category][subCategory].success.time.success /
            appealsOb[category][subCategory].success.all.length
          );
          appealsOb[category][subCategory].avgSec = Helper.roundNumber(
            appealsOb[category][subCategory].success.time.successSec /
            appealsOb[category][subCategory].success.all.length
          );
          appealsOb[category][subCategory].avgFormat = Helper.dhm(
            appealsOb[category][subCategory].avgSec
          );
          appealsOb[category][
            subCategory
          ].success.time.successFormat = Helper.dhm(
            appealsOb[category][subCategory].success.time.successSec
          );
          appealsOb.avg = Helper.roundNumber(
            appealsOb.time.success / appealsOb.successCount
          );
          appealsOb.avgSec = Helper.roundNumber(
            appealsOb.time.successSec / appealsOb.successCount
          );
        }
      }
      this.currentAppeals = appealsOb;
      return appealsOb;
    }
  }
};
</script>

<style lang="scss" scoped>
.booking-box,
.booking-box-non {
  border: 1px solid rgba(128, 128, 128, 0.2);
  padding: 10px 15px;
  margin: 0px 0 10px;
  align-items: center;
}

.booking-box:hover {
  background: rgba(128, 128, 128, 0.05);
  cursor: pointer;
}

.payment-box {
  border: 1px solid rgba(128, 128, 128, 0.5);
  padding: 10px 15px;
  margin: 0px 20px;
  align-items: center;
}

.text-nowrap-table.customer-name {
  width: 80px;
}
</style>
